import {
  ResponderMapProvider,
  ResponderTelemetry,
  ScheduleMapView,
  useResponderMap,
} from 'features/Responder';
import { PersonnelProvider } from 'features/Responder/features/Personnel';
import {
  SchedulesProvider,
  useSchedules,
} from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import RunsPanel from 'features/Responder/features/Schedules/features/Runs';
import RunEventHistoryPanel, {
  EventHistoryMapLayer,
  EventHistoryMapToolsPanel,
  getUniqueEventKinds,
  hasValidEventLatLng,
} from 'features/Responder/features/Schedules/features/RunEventHistory';
import { useRunEventHistory } from 'features/Responder/features/Schedules/hooks/useRunEventHistory';
import {
  isRunOpen,
  resolveRunEnd,
  resolveScheduleTour,
  resolveScheduleZoneIds,
} from 'features/Responder/features/Schedules/utils';
import { deriveCheckpointsFromTour } from 'features/Responder/features/Tours';
import { resolveRoutePoints } from 'features/Responder/features/Tours';
import { TourProvider, useTours } from 'features/Responder/features/Tours';
import { useZones, ZonesProvider } from 'features/Responder/features/Zones';
import { fitMapToZone } from 'features/Responder/features/Zones';
import L from 'leaflet';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useMap } from 'react-leaflet';
import { Group, Panel } from 'react-resizable-panels';
import { ResizablePanelHandle } from 'utils/panelResizeUtils';

const SelectedZoneFocus = ({ zone, zones, focusKey = null }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !zone || focusKey == null) return;
    fitMapToZone(map, zone, zones);
  }, [focusKey, map, zone, zones]);

  return null;
};

const normalizeLatLng = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 2) {
    const lat = Number(value[0]);
    const lng = Number(value[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  const lat = Number(
    value?.lat ?? value?.latitude ?? value?.coords?.[0] ?? value?.coordinates?.[0],
  );
  const lng = Number(
    value?.lng ?? value?.longitude ?? value?.coords?.[1] ?? value?.coordinates?.[1],
  );
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const resolveTourFocusPoints = (tour) => {
  if (!tour) return [];
  const routePoints = resolveRoutePoints(null, tour).filter(
    (point) =>
      Array.isArray(point) &&
      point.length >= 2 &&
      Number.isFinite(Number(point[0])) &&
      Number.isFinite(Number(point[1])),
  );
  if (routePoints.length > 1) {
    return routePoints.map((point) => [Number(point[0]), Number(point[1])]);
  }

  const collected = [];
  const seen = new Set();
  const push = (value) => {
    const next = normalizeLatLng(value);
    if (!next) return;
    const key = `${next.lat},${next.lng}`;
    if (seen.has(key)) return;
    seen.add(key);
    collected.push([next.lat, next.lng]);
  };

  const { originNode, checkpoints } = deriveCheckpointsFromTour(tour);
  push(originNode);
  (Array.isArray(checkpoints) ? checkpoints : []).forEach((checkpoint) => push(checkpoint));
  push(tour?.startLocation);
  push(tour?.originNode);
  push(tour?.origin);

  return collected;
};

const SelectedTourFocus = ({ tour, focusKey = null }) => {
  const map = useMap();
  const lastFocusedKeyRef = useRef(null);

  const focusPoints = useMemo(() => resolveTourFocusPoints(tour), [tour]);

  useEffect(() => {
    if (!map || !tour || focusKey == null || !focusPoints.length) return;
    const key = `${focusKey}|tour:${tour?.id ?? ''}`;
    if (lastFocusedKeyRef.current === key) return;

    if (focusPoints.length === 1) {
      const currentZoom = map?.getZoom?.() ?? 14;
      map.setView(focusPoints[0], Math.min(Math.max(currentZoom, 14), 17), { animate: true });
    } else {
      map.fitBounds(L.latLngBounds(focusPoints), {
        padding: [60, 60],
        maxZoom: 17,
        animate: true,
      });
    }

    lastFocusedKeyRef.current = key;
  }, [focusKey, focusPoints, map, tour]);

  return null;
};

const parseDateMs = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const toIdKey = (values = []) =>
  Array.from(new Set((Array.isArray(values) ? values : []).map((value) => `${value}`)))
    .sort()
    .join('|');

const RunsLayout = () => {
  const { tours, selectTour, activeTourData } = useTours();
  const { zonesWithMeta, activeZoneId, setActiveZoneId } = useZones();
  const { selectedZoneIds, setSelectedZoneIds, selectedTourIds, setSelectedTourIds } =
    useResponderMap();
  const { activeEditSchedule, runs, lastUpdateTime, activeRunId, setActiveRunId } = useSchedules();
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventKinds, setSelectedEventKinds] = useState(new Set());
  const [manualEventFilters, setManualEventFilters] = useState(false);
  const previousFilterRef = useRef(new Set());

  const activeZone = useMemo(
    () => zonesWithMeta.find((zone) => `${zone.id}` === `${activeZoneId}`) || null,
    [activeZoneId, zonesWithMeta],
  );

  const scheduleZoneIds = useMemo(
    () => resolveScheduleZoneIds(activeEditSchedule),
    [activeEditSchedule],
  );

  const scheduleTour = useMemo(
    () => resolveScheduleTour(activeEditSchedule, tours),
    [activeEditSchedule, tours],
  );

  const hydratedTour = useMemo(() => {
    if (activeEditSchedule?.entity?.type !== 'Tour') return null;
    if (scheduleTour) return scheduleTour;
    const targetId = activeEditSchedule?.entity?.entity_id;
    if (targetId == null) return activeTourData || null;
    if (activeTourData && `${activeTourData?.id}` === `${targetId}`) return activeTourData;
    return tours.find((tour) => `${tour?.id}` === `${targetId}`) || null;
  }, [activeEditSchedule, activeTourData, scheduleTour, tours]);

  const scheduleZoneKey = useMemo(() => toIdKey(scheduleZoneIds), [scheduleZoneIds]);
  const selectedZoneKey = useMemo(() => toIdKey(selectedZoneIds), [selectedZoneIds]);
  const hydrationKey = useMemo(() => {
    if (!activeEditSchedule) return 'none';
    const scheduleId = activeEditSchedule.id ?? 'schedule';
    const tourId = scheduleTour?.id ?? '';
    return `${scheduleId}|tour:${tourId}|zones:${scheduleZoneKey}`;
  }, [activeEditSchedule, scheduleTour, scheduleZoneKey]);
  const lastHydrationKeyRef = useRef(null);

  const shouldFocusZone = activeEditSchedule?.entity?.type === 'Zone' && scheduleZoneIds.length > 0;
  const shouldFocusTour = activeEditSchedule?.entity?.type === 'Tour' && !!hydratedTour;

  const selectedRun = useMemo(() => {
    if (!activeRunId || !Array.isArray(runs)) return null;
    return runs.find((run) => `${run.id}` === `${activeRunId}`) || null;
  }, [activeRunId, runs]);

  const isActiveRunLive = useMemo(() => isRunOpen(selectedRun), [selectedRun]);

  const supplementalNotes = useMemo(() => {
    const notes = [];
    const pushNotes = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach((item) => pushNotes(item));
        return;
      }
      if (typeof value !== 'object') return;
      const lat = Number(value.lat ?? value.latitude);
      const lng = Number(value.lng ?? value.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      notes.push(value);
    };
    pushNotes(selectedRun?.notes);
    pushNotes(selectedRun?.note);
    pushNotes(activeEditSchedule?.notes);
    pushNotes(activeEditSchedule?.note);
    return notes;
  }, [activeEditSchedule, selectedRun]);

  const {
    events: runEvents,
    loading: eventsLoading,
    error: eventsError,
    refresh: refreshEvents,
    lastUpdated: runEventsLastUpdated,
  } = useRunEventHistory({
    runId: selectedRun?.id ?? null,
    supplementalNotes,
    liveUpdatesEnabled: isActiveRunLive,
    pollIntervalMs: isActiveRunLive ? 2500 : 8000,
  });

  const runEventsForDisplay = useMemo(() => {
    if (!Array.isArray(runEvents)) return [];
    const endMs = parseDateMs(resolveRunEnd(selectedRun));
    if (!Number.isFinite(endMs) || endMs <= 0) return runEvents;
    return runEvents.filter((event) => {
      const eventMs = parseDateMs(event?.timestamp);
      if (!Number.isFinite(eventMs) || eventMs <= 0) return true;
      return eventMs <= endMs;
    });
  }, [selectedRun, runEvents]);

  const mappableEvents = useMemo(
    () =>
      Array.isArray(runEventsForDisplay)
        ? runEventsForDisplay.filter((event) => hasValidEventLatLng(event))
        : [],
    [runEventsForDisplay],
  );

  const eventKinds = useMemo(() => getUniqueEventKinds(mappableEvents), [mappableEvents]);

  useEffect(() => {
    if (manualEventFilters) return;
    setSelectedEventKinds(new Set(eventKinds));
  }, [eventKinds, manualEventFilters]);

  useEffect(() => {
    setSelectedEventId(null);
    setManualEventFilters(false);
  }, [selectedRun?.id]);

  useEffect(() => {
    if (!activeRunId) return;
    const exists = (Array.isArray(runs) ? runs : []).some(
      (run) => `${run.id}` === `${activeRunId}`,
    );
    if (!exists) setActiveRunId?.(null);
  }, [activeRunId, runs, setActiveRunId]);

  const handleToggleEventKind = useCallback((kind) => {
    setSelectedEventKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
    setSelectedEventId(null);
    setManualEventFilters(true);
  }, []);

  const handleClearEventFilters = useCallback(() => {
    setSelectedEventKinds(new Set());
    setSelectedEventId(null);
    setManualEventFilters(true);
  }, []);

  const handleSelectEvent = useCallback(
    (event) => {
      if (!event) return;
      const nextId = `${event.id}`;
      setSelectedEventId((prevId) => {
        if (`${prevId ?? ''}` === nextId) {
          const restored =
            previousFilterRef.current && previousFilterRef.current.size
              ? previousFilterRef.current
              : new Set(eventKinds);
          setSelectedEventKinds(new Set(restored));
          setManualEventFilters(true);
          return null;
        }
        previousFilterRef.current = new Set(selectedEventKinds);
        setSelectedEventKinds(new Set());
        setManualEventFilters(true);
        return event.id;
      });
    },
    [eventKinds, selectedEventKinds],
  );

  const handleClearFocus = useCallback(() => {
    setSelectedEventId(null);
    setSelectedEventKinds(new Set(eventKinds));
    setManualEventFilters(false);
  }, [eventKinds]);

  const mapOverlays = useMemo(
    () =>
      shouldFocusZone || shouldFocusTour || runEventsForDisplay.length ? (
        <>
          {shouldFocusTour ? (
            <SelectedTourFocus tour={hydratedTour} focusKey={activeEditSchedule?.id ?? null} />
          ) : null}
          {shouldFocusZone ? (
            <SelectedZoneFocus
              zone={activeZone}
              zones={zonesWithMeta}
              focusKey={activeEditSchedule?.id ?? null}
            />
          ) : null}
          <EventHistoryMapLayer
            events={runEventsForDisplay}
            selectedKinds={selectedEventKinds}
            selectedEventId={selectedEventId}
            fitKey={selectedRun?.id ?? null}
            onSelectEvent={handleSelectEvent}
          />
        </>
      ) : null,
    [
      activeEditSchedule,
      activeZone,
      hydratedTour,
      runEventsForDisplay,
      selectedEventKinds,
      selectedEventId,
      selectedRun?.id,
      handleSelectEvent,
      shouldFocusTour,
      shouldFocusZone,
      zonesWithMeta,
    ],
  );

  useEffect(() => {
    if (lastHydrationKeyRef.current === hydrationKey) return;
    lastHydrationKeyRef.current = hydrationKey;

    if (!activeEditSchedule) {
      if (activeTourData) selectTour(null);
      if (selectedTourIds.length) setSelectedTourIds([]);
      if (activeZoneId != null || selectedZoneIds.length) {
        setActiveZoneId(null);
        setSelectedZoneIds([]);
      }
      return;
    }

    const scheduleTourId = scheduleTour?.id ?? null;
    const activeTourId = activeTourData?.id ?? null;
    if (scheduleTour) {
      selectTour(scheduleTour);
    } else if (activeTourId != null) {
      selectTour(null);
    }
    const nextTourIds = scheduleTourId != null ? [scheduleTourId] : [];
    if (toIdKey(nextTourIds) !== toIdKey(selectedTourIds)) {
      setSelectedTourIds(nextTourIds);
    }
    if (activeEditSchedule.entity?.type === 'Zone') {
      const nextZoneId = scheduleZoneIds[0] ?? null;
      const zoneSelectionChanged =
        scheduleZoneKey !== selectedZoneKey || `${activeZoneId ?? ''}` !== `${nextZoneId ?? ''}`;
      if (zoneSelectionChanged) {
        if (scheduleZoneIds.length) {
          setActiveZoneId(nextZoneId);
          setSelectedZoneIds(scheduleZoneIds);
        } else {
          setActiveZoneId(null);
          setSelectedZoneIds([]);
        }
      }
    } else if (activeZoneId != null || selectedZoneIds.length) {
      setActiveZoneId(null);
      setSelectedZoneIds([]);
    }
  }, [
    activeEditSchedule,
    activeTourData,
    activeZoneId,
    hydrationKey,
    scheduleTour,
    scheduleZoneIds,
    scheduleZoneKey,
    selectTour,
    selectedTourIds,
    selectedZoneIds,
    selectedZoneIds.length,
    selectedZoneKey,
    setActiveZoneId,
    setSelectedTourIds,
    setSelectedZoneIds,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateHeight = () => {
      if (!pageRef.current) return;
      const topOffset = pageRef.current.getBoundingClientRect().top || 0;
      const nextHeight = Math.max(window.innerHeight - topOffset, 320);
      setPageHeight(nextHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return (
    <div
      ref={pageRef}
      className="tour-builder-page py-4"
      style={{
        minHeight: 0,
        height: pageHeight ? `${pageHeight}px` : '100vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Container fluid className="d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
        <Row className="flex-grow-1" style={{ minHeight: 0 }}>
          <Col className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
            <Group
              orientation="vertical"
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              <Panel
                defaultSize={65}
                minSize={35}
                className="d-flex flex-column h-100"
                style={{ minHeight: 0 }}
              >
                <Group
                  orientation="horizontal"
                  className="d-flex w-100 h-100"
                  style={{ minHeight: 0 }}
                >
                  <Panel
                    defaultSize={65}
                    minSize={40}
                    className="d-flex flex-column h-100"
                    style={{ minHeight: 0 }}
                  >
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div className="card-body rounded overflow-hidden p-0 h-100">
                        <div className="position-relative w-100 h-100">
                          <div className="w-100 h-100 rounded-top overflow-hidden">
                            <ScheduleMapView extraLayers={mapOverlays} showPersonnel />
                          </div>
                          <div
                            className="position-absolute top-0 end-0 p-2"
                            style={{
                              zIndex: 800,
                              maxWidth: 260,
                              maxHeight: '92%',
                              overflowY: 'auto',
                            }}
                          >
                            <EventHistoryMapToolsPanel
                              events={runEventsForDisplay}
                              selectedKinds={selectedEventKinds}
                              selectedEventId={selectedEventId}
                              onToggleKind={handleToggleEventKind}
                              onClearFilters={handleClearEventFilters}
                              onClearSelection={handleClearFocus}
                              lastUpdated={runEventsLastUpdated || lastUpdateTime}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Panel>
                  <ResizablePanelHandle direction="vertical" />
                  <Panel
                    defaultSize={35}
                    minSize={25}
                    className="d-flex flex-column h-100"
                    style={{ minHeight: 0 }}
                  >
                    <RunEventHistoryPanel
                      schedule={activeEditSchedule}
                      run={selectedRun}
                      events={runEventsForDisplay}
                      loading={eventsLoading}
                      error={eventsError}
                      selectedEventId={selectedEventId}
                      onSelectEvent={handleSelectEvent}
                      onRefresh={isActiveRunLive ? refreshEvents : null}
                    />
                  </Panel>
                </Group>
              </Panel>

              <ResizablePanelHandle direction="horizontal" />

              <Panel
                defaultSize={35}
                minSize={25}
                className="d-flex flex-column h-100"
                style={{ minHeight: 0 }}
              >
                <Group
                  orientation="horizontal"
                  className="d-flex flex-row h-100"
                  style={{ minHeight: 0 }}
                >
                  <Panel
                    defaultSize={50}
                    minSize={25}
                    className="d-flex flex-column h-100"
                    style={{ minHeight: 0 }}
                  >
                    <div className="card border-0 shadow-lg text-white h-100 bg-dark">
                      <div
                        className="card-body h-100 d-flex flex-column p-0"
                        style={{ minHeight: 0 }}
                      >
                        <RunsPanel />
                      </div>
                    </div>
                  </Panel>
                  <ResizablePanelHandle direction="vertical" />
                  <Panel
                    defaultSize={50}
                    minSize={25}
                    className="d-flex flex-column h-100"
                    style={{ minHeight: 0 }}
                  >
                    <div className="card border-0 shadow-lg text-white h-100 bg-dark">
                      <div
                        className="card-body h-100 d-flex flex-column p-2"
                        style={{ minHeight: 0 }}
                      >
                        <ResponderTelemetry
                          scrollHeight="100%"
                          allowedScopes={['default']}
                          showScopeTabs={false}
                        />
                      </div>
                    </div>
                  </Panel>
                </Group>
              </Panel>
            </Group>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default function Runs() {
  return (
    <PersonnelProvider>
      <TourProvider>
        <ZonesProvider>
          <ResponderMapProvider>
            <SchedulesProvider>
              <RunsLayout />
            </SchedulesProvider>
          </ResponderMapProvider>
        </ZonesProvider>
      </TourProvider>
    </PersonnelProvider>
  );
}


